import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import ModernEmailBuilder from './emailBuilder/ModernEmailBuilder';
import { EmailTemplate as BuilderTemplate } from '../types/emailBuilder';
import { emailTemplateService, EmailTemplate } from '../services/emailTemplateService';
import { createEmailTemplate } from '../services/emailTemplates';
import { ensureTemplateHtml } from '../utils/templateHtml';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { usePageTitle } from '../hooks/usePageTitle';

const AdminCreateEmailTemplatePage: React.FC = () => {
  usePageTitle('Create Email Template');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDark } = useAdminTheme();
  const { templateId: routeTemplateId } = useParams<{ templateId?: string }>();
  
  const [templateCategory, setTemplateCategory] = useState<string>('campaign');
  const [templateDescription, setTemplateDescription] = useState<string>('');
  const isFirstSaveRef = useRef(true);
  const lastSavedTemplateIdRef = useRef<string | null>(null);
  const isAutoSavingRef = useRef(false);
  const lastAutoSaveTimeRef = useRef<number>(Date.now()); // Initialize to current time
  const [savedTemplateId, setSavedTemplateId] = useState<string | null>(null);
  const pendingTemplateIdRef = useRef<string | null>(routeTemplateId ?? null);
  const hasCreatedPendingTemplateRef = useRef(false);
  
  // Initialize lastAutoSaveTimeRef on mount to prevent first save from being misclassified
  React.useEffect(() => {
    lastAutoSaveTimeRef.current = Date.now();
  }, []);

  const handleBuilderSave = async (template: BuilderTemplate) => {
    if (!currentUser) {
      return;
    }

    // Detect if this is an auto-save
    // Autosaves happen frequently (within 5 seconds of last save)
    // Manual saves are user-initiated button clicks (usually more than 5 seconds apart)
    const now = Date.now();
    const timeSinceLastSave = now - lastAutoSaveTimeRef.current;
    
    // If this save happens within 5 seconds of the last save, it's likely an autosave
    // Also check if we're already in an autosave cycle
    const isAutoSave = timeSinceLastSave < 5000 || isAutoSavingRef.current;
    
    if (isAutoSave) {
      isAutoSavingRef.current = true;
    }
    
    lastAutoSaveTimeRef.current = now;

    try {
      const htmlContent = await ensureTemplateHtml(template);
      const builderState: BuilderTemplate = { ...template, htmlContent };

      const templateData = {
        name: template.name || 'Untitled Admin Template',
        subject: template.subject || 'Admin created template',
        htmlContent,
        textContent: template.subject || 'Admin created template',
        category: templateCategory as any,
        tags: ['admin-created'],
        isActive: true,
        createdBy: 'admin',
        variables: ['first_name', 'last_name', 'email', 'company_name', 'website_url', 'unsubscribe_url'],
        description: templateDescription || 'Admin created template',
        isPaid: false,
        designJson: builderState,
      };
      
      let templateId: string;
      
      if (savedTemplateId) {
        await emailTemplateService.updateTemplate(savedTemplateId, {
          name: templateData.name,
          subject: templateData.subject,
          description: templateData.description,
          htmlContent: templateData.htmlContent,
          textBody: templateData.textContent,
          category: templateData.category,
          tags: templateData.tags,
          isActive: templateData.isActive,
          variables: templateData.variables,
          designJson: builderState,
        });
        templateId = savedTemplateId;
      } else if (pendingTemplateIdRef.current && !hasCreatedPendingTemplateRef.current) {
        const targetId = pendingTemplateIdRef.current;
        await setDoc(doc(db, 'email_templates', targetId), {
          ...templateData,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        hasCreatedPendingTemplateRef.current = true;
        templateId = targetId;
        lastSavedTemplateIdRef.current = targetId;
        setSavedTemplateId(targetId);
        pendingTemplateIdRef.current = targetId;
        if (isFirstSaveRef.current) {
          isFirstSaveRef.current = false;
        }
      } else {
        templateId = await createEmailTemplate(templateData);
        lastSavedTemplateIdRef.current = templateId;
        setSavedTemplateId(templateId);
        
        if (isFirstSaveRef.current) {
          isFirstSaveRef.current = false;
        }
      }
      
      // Reset auto-save flag after a delay
      if (isAutoSave) {
        setTimeout(() => {
          isAutoSavingRef.current = false;
        }, 5000);
      }
      
      // NEVER navigate on autosave - only navigate on explicit manual save
      // Since ModernEmailBuilder calls onSave for both autosave and manual save,
      // we detect autosave by timing (saves within 5 seconds are autosaves).
      // We never navigate automatically to prevent unwanted redirects.
      // If user wants to navigate after saving, they should use a separate action.
    } catch (error) {
      console.error('Error saving template:', error);
      alert(`Error saving template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBuilderSaveAndNext = async (template: BuilderTemplate) => {
    if (!currentUser) {
      alert('You must be logged in to save templates');
      return;
    }

    try {
      const htmlContent = await ensureTemplateHtml(template);
      const builderState: BuilderTemplate = { ...template, htmlContent };

      const templateData = {
        name: template.name || 'Untitled Admin Template',
        subject: template.subject || 'Admin created template',
        htmlContent,
        textContent: template.subject || 'Admin created template',
        category: templateCategory as any,
        tags: ['admin-created'],
        isActive: true,
        createdBy: 'admin',
        variables: ['first_name', 'last_name', 'email', 'company_name', 'website_url', 'unsubscribe_url'],
        description: templateDescription || 'Admin created template',
        isPaid: false,
        designJson: builderState,
      };
      
      let templateId: string;
      
      if (savedTemplateId) {
        await emailTemplateService.updateTemplate(savedTemplateId, {
          name: templateData.name,
          subject: templateData.subject,
          description: templateData.description,
          htmlContent: templateData.htmlContent,
          textBody: templateData.textContent,
          category: templateData.category,
          tags: templateData.tags,
          isActive: templateData.isActive,
          variables: templateData.variables,
          designJson: builderState,
        });
        templateId = savedTemplateId;
      } else if (pendingTemplateIdRef.current && !hasCreatedPendingTemplateRef.current) {
        const targetId = pendingTemplateIdRef.current;
        await setDoc(doc(db, 'email_templates', targetId), {
          ...templateData,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        hasCreatedPendingTemplateRef.current = true;
        templateId = targetId;
        lastSavedTemplateIdRef.current = targetId;
        setSavedTemplateId(targetId);
        pendingTemplateIdRef.current = targetId;
        if (isFirstSaveRef.current) {
          isFirstSaveRef.current = false;
        }
      } else {
        templateId = await createEmailTemplate(templateData);
        lastSavedTemplateIdRef.current = templateId;
        setSavedTemplateId(templateId);
        
        if (isFirstSaveRef.current) {
          isFirstSaveRef.current = false;
        }
      }
      
      // Navigate to template details page
      navigate(`/admin/control-panel-7x9k2m/email-templates/${templateId}/details`);
    } catch (error) {
      console.error('Error saving template:', error);
      alert(`Error saving template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBuilderCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      navigate('/admin/control-panel-7x9k2m/email-templates');
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/control-panel-7x9k2m/email-templates')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ← Back to Templates
              </button>
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Create New Email Template
                </h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Design your email template using the builder below
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Category Selector */}
              <div className="flex items-center space-x-2">
                <label className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Category:
                </label>
                <select
                  value={templateCategory}
                  onChange={(e) => setTemplateCategory(e.target.value)}
                  className={`px-3 py-2 rounded-lg border text-sm ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="campaign">Campaign</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="welcome">Welcome</option>
                  <option value="transactional">Transactional</option>
                  <option value="automation">Automation</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="marketing">Marketing</option>
                  <option value="social">Social</option>
                  <option value="educational">Educational</option>
                  <option value="seasonal">Seasonal</option>
                  <option value="event">Event</option>
                  <option value="product">Product</option>
                  <option value="announcement">Announcement</option>
                  <option value="follow-up">Follow-up</option>
                  <option value="reminder">Reminder</option>
                  <option value="thank-you">Thank You</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="retention">Retention</option>
                  <option value="real-estate">Real Estate</option>
                  <option value="inspirational">Inspirational</option>
                  <option value="basic">Basic</option>
                </select>
              </div>
              
              {/* Description Input */}
              <div className="flex items-center space-x-2">
                <label className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Description:
                </label>
                <input
                  type="text"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Template description"
                  className={`px-3 py-2 rounded-lg border text-sm ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Builder Content */}
      <div className="h-[calc(100vh-80px)]">
        <ModernEmailBuilder
          template={undefined} // New template, no existing template
          campaignName="New Admin Template"
          onSave={handleBuilderSave}
          onSaveAndNext={handleBuilderSaveAndNext}
          onCancel={handleBuilderCancel}
        />
      </div>
    </div>
  );
};

export default AdminCreateEmailTemplatePage;

